import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const LanguageContainer = styled.div`
  position: relative;
  display: inline-block;

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  background: transparent;
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  transition: all var(--transition-normal);
  min-width: 80px;
  white-space: nowrap;
  outline: none;

  &:hover {
    background: var(--color-gray-100);
    border-color: var(--color-primary);
    color: var(--color-text-primary);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  }

  &:focus {
    background: var(--color-gray-100);
    border-color: var(--color-primary);
    color: var(--color-text-primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    padding: 1rem 1.5rem;
    font-size: var(--text-sm);
    min-width: auto;
    border: none;
    background: transparent;
    border-radius: 0;
    
    &:hover {
      background: var(--color-gray-50);
      border-color: transparent;
      transform: none;
    }
  }
`;

const LanguageDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: var(--spacing-xs);
  background: var(--color-background-paper);
  border: 1px solid var(--color-border-light);
  border-radius: var(--border-radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 140px;
  z-index: 1120;
  opacity: ${props => props.$isOpen ? '1' : '0'};
  transform: ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.95)'};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);

  /* Fix positioning inside mobile menu */
  @media (max-width: 768px) {
    position: fixed;
    top: auto;
    bottom: auto;
    left: 20px;
    right: 20px;
    margin-top: 0.5rem;
    z-index: 1300; /* Much higher than mobile menu */
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2);
    min-width: auto;
    width: auto;
    max-width: 280px;
    backdrop-filter: blur(12px);
    border: 2px solid var(--color-primary);
  }
`;

const LanguageOption = styled.button<{ $isActive?: boolean }>`
  width: 100%;
  text-align: left;
  padding: var(--spacing-sm) var(--spacing-md);
  background: ${props => props.$isActive ? 'var(--color-primary-light)' : 'transparent'};
  border: none;
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all 0.15s ease;
  border-bottom: 1px solid var(--color-border-light);
  outline: none;
  margin: 2px;
  border-radius: var(--border-radius-sm);

  &:hover {
    background: ${props => props.$isActive ? 'var(--color-primary-light)' : 'var(--color-gray-50)'};
    transform: translateX(2px);
    color: var(--color-text-primary);
  }

  &:focus {
    background: var(--color-gray-100);
    box-shadow: inset 0 0 0 1px var(--color-primary);
  }

  &:active {
    background: var(--color-gray-200);
    transform: translateX(0);
  }

  &:last-child {
    border-bottom: none;
  }

  &:first-child {
    border-top-left-radius: var(--border-radius-md);
    border-top-right-radius: var(--border-radius-md);
  }

  &:last-child {
    border-bottom-left-radius: var(--border-radius-md);
    border-bottom-right-radius: var(--border-radius-md);
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: var(--text-sm);
    
    &:hover {
      transform: none;
    }
  }
`;

const FlagIcon = styled.span`
  font-size: 1.2em;
  margin-right: var(--spacing-xs);
`;

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'bg', name: 'Български', flag: '🇧🇬' }
];

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  
  const currentLanguage = languages.find(lang => i18n.language.startsWith(lang.code)) || languages[0];

  // Calculate dropdown position for mobile
  useEffect(() => {
    if (isOpen && containerRef.current && window.innerWidth <= 768) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 120; // Estimated dropdown height
      
      let top = rect.bottom + 8; // 8px margin
      
      // If dropdown would go off screen, position above the button
      if (top + dropdownHeight > viewportHeight - 20) {
        top = rect.top - dropdownHeight - 8;
      }
      
      setDropdownStyle({
        position: 'fixed',
        top: `${top}px`,
        left: '20px',
        right: '20px',
        zIndex: 1300
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode).then(() => {
      window.location.reload();
    });
    setIsOpen(false);
  };

  return (
    <LanguageContainer ref={containerRef}>
      <LanguageButton
        onClick={() => setIsOpen(!isOpen)}
      >
        <FlagIcon>{currentLanguage.flag}</FlagIcon>
        {currentLanguage.code.toUpperCase()}
        <span style={{ fontSize: '0.8em' }}>▼</span>
      </LanguageButton>
      
      <LanguageDropdown 
        ref={dropdownRef}
        $isOpen={isOpen}
        style={window.innerWidth <= 768 ? dropdownStyle : {}}
      >
        {languages.map((language) => (
          <LanguageOption
            key={language.code}
            $isActive={language.code === i18n.language}
            onClick={() => handleLanguageChange(language.code)}
          >
            <FlagIcon>{language.flag}</FlagIcon>
            {language.name}
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageContainer>
  );
};

export default LanguageSwitcher;